// src/pages/Settings/SettingsView.tsx

import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
import {
    Save, DollarSign, Phone, Smartphone,
    Bell, Shield, RefreshCw, CheckCircle,
    AlertCircle, Wallet, Settings as SettingsIcon,
    Server
} from 'lucide-react'

// أنواع البيانات
interface ExchangeRate {
    id: number
    name: string
    code: string
    rate_lbp: number
    description: string
    updated_at: string
}

interface ThemeSetting {
    id: number
    key: string
    value: string
    type: 'text' | 'color' | 'image' | 'font' | 'number' | 'boolean'
    category: 'general' | 'colors' | 'fonts' | 'images' | 'layout' | 'operators'
    description: string
    updated_at: string
}

interface UssdConfig {
    id: number
    name: string
    code: string
    value: string
    description: string
    updated_at: string
}

type ActiveTab = 'rates' | 'wallets' | 'theme' | 'ussd' | 'system'

interface SettingsViewProps {
    exchangeRates: ExchangeRate[]
    themeSettings: any // مؤقتاً أي (any) لأن الشكل مختلف
    ussdConfig: UssdConfig[]
    loading: boolean
    saving: Record<string, boolean>
    messages: Record<string, {type: 'success' | 'error', text: string}>
    activeTab: ActiveTab
    sidebarOpen: boolean
    toastMessage: {type: 'success' | 'error', text: string} | null
    handleRateChange: (code: string, value: string) => void
    handleThemeChange: (key: string, value: string) => void
    handleUssdChange: (code: string, value: string) => void
    handleSaveRate: (code: string) => Promise<void>
    handleSaveTheme: (key: string) => Promise<void>
    handleSaveUssd: (code: string) => Promise<void>
    toggleSidebar: () => void
    handleTabChange: (tab: ActiveTab) => void
    notificationsEnabled?: boolean
    twoFAEnabled?: boolean
    autoRefreshEnabled?: boolean
    handleToggleNotifications?: (value: boolean) => Promise<void>
    handleToggle2FA?: (value: boolean) => Promise<void>
    handleToggleAutoRefresh?: (value: boolean) => Promise<void>
}

export const SettingsView: React.FC<SettingsViewProps> = ({
    exchangeRates,
    themeSettings,
    ussdConfig,
    loading,
    saving,
    messages,
    activeTab,
    sidebarOpen,
    toastMessage,
    handleRateChange,
    handleThemeChange,
    handleUssdChange,
    handleSaveRate,
    handleSaveTheme,
    handleSaveUssd,
    toggleSidebar,
    handleTabChange,
    notificationsEnabled = false,
    twoFAEnabled = false,
    autoRefreshEnabled = false,
    handleToggleNotifications = async () => { },
    handleToggle2FA = async () => { },
    handleToggleAutoRefresh = async () => { }
}) => {
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mr-4 text-gray-600">جاري تحميل الإعدادات...</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />

            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => toggleSidebar()}
                isMobile={true}
            />

            <div className="hidden lg:block">
                <Sidebar isMobile={false} />
            </div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <SettingsIcon className="w-6 h-6 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">الإعدادات</h1>
                            <p className="text-gray-500 mt-1">إعدادات النظام والأسعار والثيم</p>
                        </div>
                    </div>

                    {/* Toast Message */}
                    {toastMessage && (
                        <div className="mb-4 animate-fade-in-down">
                            <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${toastMessage.type === 'success'
                                ? 'bg-green-500 text-white'
                                : 'bg-red-500 text-white'
                                }`}>
                                {toastMessage.type === 'success'
                                    ? <CheckCircle className="w-5 h-5" />
                                    : <AlertCircle className="w-5 h-5" />
                                }
                                <span className="font-medium">{toastMessage.text}</span>
                            </div>
                        </div>
                    )}

                    {/* رسالة عامة قديمة (للتوافق) */}
                    {messages.global && !toastMessage && (
                        <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${messages.global.type === 'success'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                            }`}>
                            {messages.global.type === 'success'
                                ? <CheckCircle className="w-5 h-5" />
                                : <AlertCircle className="w-5 h-5" />
                            }
                            {messages.global.text}
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto pb-1">
                        <TabButton
                            active={activeTab === 'rates'}
                            onClick={() => handleTabChange('rates')}
                            icon={<DollarSign className="w-4 h-4" />}
                            label="أسعار الصرف"
                        />
                        <TabButton
                            active={activeTab === 'wallets'}
                            onClick={() => handleTabChange('wallets')}
                            icon={<Wallet className="w-4 h-4" />}
                            label="المحافظ"
                        />
                        <TabButton
                            active={activeTab === 'theme'}
                            onClick={() => handleTabChange('theme')}
                            icon={<Smartphone className="w-4 h-4" />}
                            label="الثيم"
                        />
                        <TabButton
                            active={activeTab === 'ussd'}
                            onClick={() => handleTabChange('ussd')}
                            icon={<Phone className="w-4 h-4" />}
                            label="USSD"
                        />
                        <TabButton
                            active={activeTab === 'system'}
                            onClick={() => handleTabChange('system')}
                            icon={<Server className="w-4 h-4" />}
                            label="النظام"
                        />
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'rates' && (
                        <RatesTab
                            exchangeRates={exchangeRates}
                            saving={saving}
                            messages={messages}
                            handleRateChange={handleRateChange}
                            handleSaveRate={handleSaveRate}
                        />
                    )}

                    {activeTab === 'wallets' && <WalletsTab />}

                    {activeTab === 'theme' && (
                        <ThemeTab
                            themeSettings={themeSettings}
                            saving={saving}
                            messages={messages}
                            handleThemeChange={handleThemeChange}
                            handleSaveTheme={handleSaveTheme}
                        />
                    )}

                    {activeTab === 'ussd' && (
                        <UssdTab
                            ussdConfig={ussdConfig}
                            saving={saving}
                            messages={messages}
                            handleUssdChange={handleUssdChange}
                            handleSaveUssd={handleSaveUssd}
                        />
                    )}

                    {activeTab === 'system' && (
                        <SystemTab
                            notificationsEnabled={notificationsEnabled}
                            twoFAEnabled={twoFAEnabled}
                            autoRefreshEnabled={autoRefreshEnabled}
                            handleToggleNotifications={handleToggleNotifications}
                            handleToggle2FA={handleToggle2FA}
                            handleToggleAutoRefresh={handleToggleAutoRefresh}
                        />
                    )}
                </div>
            </main>
        </div>
    )
}

// Tab Button Component
const TabButton: React.FC<{active: boolean; onClick: () => void; icon: React.ReactNode; label: string}> = ({
    active, onClick, icon, label
}) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${active
            ? 'text-blue-600 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
    >
        {icon}
        {label}
    </button>
)

// Rates Tab
const RatesTab: React.FC<{
    exchangeRates: ExchangeRate[]
    saving: Record<string, boolean>
    messages: Record<string, {type: 'success' | 'error', text: string}>
    handleRateChange: (code: string, value: string) => void
    handleSaveRate: (code: string) => Promise<void>
}> = ({exchangeRates, saving, messages, handleRateChange, handleSaveRate}) => {
    if (!exchangeRates || exchangeRates.length === 0) {
        return (
            <Card title="أسعار الصرف">
                <div className="p-8 text-center">
                    <p className="text-gray-500">لا توجد بيانات أسعار صرف</p>
                </div>
            </Card>
        )
    }

    return (
        <Card title="أسعار الصرف" subtitle="تحديث أسعار الشراء والبيع">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {exchangeRates.map((rate) => (
                    <div key={rate.code} className="bg-gray-50 p-4 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {rate.name} ({rate.code})
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                value={rate.rate_lbp}
                                onChange={(e) => handleRateChange(rate.code, e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="السعر"
                                dir="ltr"
                            />
                            <Button
                                onClick={() => handleSaveRate(rate.code)}
                                loading={saving[rate.code]}
                                icon={<Save className="w-4 h-4" />}
                                size="sm"
                            >
                                حفظ
                            </Button>
                        </div>
                        {messages[rate.code] && (
                            <p className={`mt-2 text-sm ${messages[rate.code].type === 'success'
                                ? 'text-green-600'
                                : 'text-red-600'
                                }`}>
                                {messages[rate.code].type === 'success' ? '✓' : '⚠'} {messages[rate.code].text}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    )
}

// Wallets Tab
const WalletsTab: React.FC = () => (
    <Card title="أرقام المحافظ" subtitle="أرقام محافظ الشركة">
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="flex items-center gap-4">
                    <div className="bg-blue-500 p-3 rounded-full">
                        <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <p className="text-sm text-blue-600 font-medium">محفظة Alfa</p>
                        <p className="text-2xl font-bold text-blue-900 font-mono">70229440</p>
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6">
                <div className="flex items-center gap-4">
                    <div className="bg-green-500 p-3 rounded-full">
                        <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <p className="text-sm text-green-600 font-medium">محفظة Touch</p>
                        <p className="text-2xl font-bold text-green-900 font-mono">03955088</p>
                    </div>
                </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 text-sm text-yellow-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                هذه الأرقام للقراءة فقط ويتم استخدامها في عمليات الشراء والبيع
            </div>
        </div>
    </Card>
)

// Theme Tab - النسخة المعدلة (بتعامل مع الكائن)
const ThemeTab: React.FC<{
    themeSettings: any
    saving: Record<string, boolean>
    messages: Record<string, {type: 'success' | 'error', text: string}>
    handleThemeChange: (key: string, value: string) => void
    handleSaveTheme: (key: string) => Promise<void>
}> = ({themeSettings, saving, messages, handleThemeChange, handleSaveTheme}) => {
    console.log('🎨 ThemeTab rendered with:', themeSettings)

    // تحويل الكائن إلى مصفوفة مسطحة
    const flattenThemeSettings = (): ThemeSetting[] => {
        if (!themeSettings) return []

        const result: ThemeSetting[] = []

        // إذا كان الكائن فيه خصائص مثل general, colors, fonts, ...
        Object.keys(themeSettings).forEach(category => {
            const items = themeSettings[category]
            if (Array.isArray(items)) {
                result.push(...items)
            }
        })

        return result
    }

    const flattenedSettings = flattenThemeSettings()
    console.log('🎨 Flattened settings:', flattenedSettings)

    if (flattenedSettings.length === 0) {
        return (
            <Card title="إعدادات الثيم">
                <div className="p-8 text-center">
                    <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 text-lg">لا توجد إعدادات ثيم</p>
                </div>
            </Card>
        )
    }

    const filteredSettings = flattenedSettings.filter(
        setting => setting.category === 'colors' || setting.category === 'fonts' || setting.category === 'images'
    )

    return (
        <Card title="إعدادات الثيم" subtitle="تخصيص شكل التطبيق">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredSettings.map((setting) => (
                    <div key={setting.key} className="bg-gray-50 p-4 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {setting.description}
                        </label>
                        <div className="flex gap-2">
                            {setting.type === 'color' ? (
                                <div className="flex-1 flex gap-2">
                                    <input
                                        type="color"
                                        value={setting.value}
                                        onChange={(e) => handleThemeChange(setting.key, e.target.value)}
                                        className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={setting.value}
                                        onChange={(e) => handleThemeChange(setting.key, e.target.value)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder={setting.description}
                                    />
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={setting.value}
                                    onChange={(e) => handleThemeChange(setting.key, e.target.value)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={setting.description}
                                />
                            )}
                            <Button
                                onClick={() => handleSaveTheme(setting.key)}
                                loading={saving[setting.key]}
                                icon={<Save className="w-4 h-4" />}
                                size="sm"
                            >
                                حفظ
                            </Button>
                        </div>
                        {messages[setting.key] && (
                            <p className={`mt-2 text-sm ${messages[setting.key].type === 'success'
                                ? 'text-green-600'
                                : 'text-red-600'
                                }`}>
                                {messages[setting.key].type === 'success' ? '✓' : '⚠'} {messages[setting.key].text}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    )
}

// USSD Tab
const UssdTab: React.FC<{
    ussdConfig: UssdConfig[]
    saving: Record<string, boolean>
    messages: Record<string, {type: 'success' | 'error', text: string}>
    handleUssdChange: (code: string, value: string) => void
    handleSaveUssd: (code: string) => Promise<void>
}> = ({ussdConfig, saving, messages, handleUssdChange, handleSaveUssd}) => {
    if (!ussdConfig || ussdConfig.length === 0) {
        return (
            <Card title="إعدادات USSD">
                <div className="p-8 text-center">
                    <p className="text-gray-500">لا توجد بيانات USSD</p>
                </div>
            </Card>
        )
    }

    return (
        <Card title="إعدادات USSD" subtitle="تكوين أرقام ورسائل USSD">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {ussdConfig.map((config) => (
                    <div key={config.code} className="bg-gray-50 p-4 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {config.name}
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={config.value}
                                onChange={(e) => handleUssdChange(config.code, e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder={config.description}
                            />
                            <Button
                                onClick={() => handleSaveUssd(config.code)}
                                loading={saving[config.code]}
                                icon={<Save className="w-4 h-4" />}
                                size="sm"
                            >
                                حفظ
                            </Button>
                        </div>
                        {messages[config.code] && (
                            <p className={`mt-2 text-sm ${messages[config.code].type === 'success'
                                ? 'text-green-600'
                                : 'text-red-600'
                                }`}>
                                {messages[config.code].type === 'success' ? '✓' : '⚠'} {messages[config.code].text}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    )
}

// System Tab
const SystemTab: React.FC<{
    notificationsEnabled: boolean
    twoFAEnabled: boolean
    autoRefreshEnabled: boolean
    handleToggleNotifications: (value: boolean) => Promise<void>
    handleToggle2FA: (value: boolean) => Promise<void>
    handleToggleAutoRefresh: (value: boolean) => Promise<void>
}> = ({
    notificationsEnabled,
    twoFAEnabled,
    autoRefreshEnabled,
    handleToggleNotifications,
    handleToggle2FA,
    handleToggleAutoRefresh
}) => (
        <div className="space-y-6">
            <Card title="إعدادات النظام">
                <div className="space-y-4">
                    <ToggleItem
                        icon={<Bell className="w-5 h-5 text-gray-500" />}
                        title="الإشعارات"
                        description="تفعيل إشعارات المعاملات"
                        checked={notificationsEnabled}
                        onChange={handleToggleNotifications}
                    />
                    <ToggleItem
                        icon={<Shield className="w-5 h-5 text-gray-500" />}
                        title="المصادقة الثنائية"
                        description="تفعيل 2FA لحماية الحساب"
                        checked={twoFAEnabled}
                        onChange={handleToggle2FA}
                    />
                    <ToggleItem
                        icon={<RefreshCw className="w-5 h-5 text-gray-500" />}
                        title="التحديث التلقائي"
                        description="تحديث البيانات كل 30 ثانية"
                        checked={autoRefreshEnabled}
                        onChange={handleToggleAutoRefresh}
                    />
                </div>
            </Card>

            <Card title="معلومات النظام">
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">الإصدار</p>
                        <p className="text-lg font-semibold text-gray-900">1.0.0</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">آخر تحديث</p>
                        <p className="text-lg font-semibold text-gray-900">{new Date().toLocaleDateString('ar-LB')}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">نوع الخادم</p>
                        <p className="text-lg font-semibold text-gray-900">Cloudflare Workers</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">حالة API</p>
                        <p className="text-lg font-semibold text-green-600">متصل</p>
                    </div>
                </div>
            </Card>
        </div>
    )

// Toggle Item Component
const ToggleItem: React.FC<{
    icon: React.ReactNode
    title: string
    description: string
    checked: boolean
    onChange: (value: boolean) => Promise<void>
}> = ({icon, title, description, checked, onChange}) => {
    const [loading, setLoading] = React.useState(false)

    const handleToggle = async () => {
        setLoading(true)
        try {
            await onChange(!checked)
        } catch (error) {
            console.error('Toggle error:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
                {icon}
                <div>
                    <p className="font-medium text-gray-900">{title}</p>
                    <p className="text-sm text-gray-500">{description}</p>
                </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    onChange={handleToggle}
                    disabled={loading}
                />
                <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors ${loading ? 'opacity-50 cursor-wait' : ''
                    }`}></div>
            </label>
        </div>
    )
}